import { useCallback, useEffect, useRef, useState } from "react"

import { IFsNodeSchema } from "@cloudike/web_files"
import classNames from "classnames"
import { boardsActions } from "features/boards/model/slice/boardsSlice"
import { BoardAssetsToolbarAction } from "shared/constants/toolbar"
import { useDoubleClick } from "features/common/hooks/useDoubleClick"
import { getDraggedItems } from "features/common/model/selectors/dragSelectors"
import { endDrag, startDrag } from "features/common/model/slice/dragSlice"
import { getFilesPreviewIsPublicLinkSelector } from "features/files-preview/model/selectors/filesPreviewSelectors"
import { openFilesPreviewThunk } from "features/files-preview/model/slice/filesPreviewSlice"
import { useGetEnablePermission } from "features/public-link-files/hooks/useGetEnablePermission"
import { MemberRole } from "features/team/model/types/members"
import { getUserRole } from "features/user/model/selectors/userSelectors"
import _ from "lodash"
import { useNavigate } from "react-router-dom"
import { AutoSizer, List, WindowScroller } from "react-virtualized"
import { useAppDispatch, useAppSelector } from "shared/config/store"
import { Button } from 'shared/ui'
import { hasAccess } from "shared/utils/access"
import { checkPermission } from "shared/utils/checkPermission"
import { toMb } from "shared/utils/filesUtils"
import styled from "styled-components"
import { useTranslation } from "react-i18next"
import { UseAssetForAiAssistantSwitch } from "features/ai-assistant/ui/UseAssetForAiAssistantSwitch"

import {
  AppstoreAddOutlined,
  ArrowRightOutlined,
  ArrowsAltOutlined,
  AudioOutlined,
  DeleteOutlined,
  DownloadOutlined,
  Dropdown,
  EditOutlined,
  EllipsisOutlined,
  FileImageOutlined,
  FileOutlined,
  LinkOutlined,
  MenuProps,
  MinusCircleOutlined,
  VideoCameraOutlined,
  DeploymentUnitOutlined,
} from '../../../shared/ui'
import { DragPreview } from "../../common/ui/DragPreview"
import { assetsActions } from "../model/slice/assetsSlice"
import { FILES_EXTANSIONS_FOR_AI_ASSISTANT_INDEXING } from "../model/types/assets"

interface EnhancedFsNodeSchema extends IFsNodeSchema {
  use_for_rag: boolean;
  ext: string;
  _embedded?: {
    previews?: {
      url_template: string;
      jwt: string;
      sizes?: {
        middle?: boolean;
      };
    };
  };
}

function getSmallPreviewLink(item: EnhancedFsNodeSchema) {
  if (!item?._embedded?.previews?.sizes?.middle) {
    return ''
  }

  const urlTemplate = _.template(item._embedded.previews.url_template, { interpolate: /\{(.+?)\}/g })
  const url = urlTemplate({ size_name: 'middle' }) + `&jwt=${item._embedded.previews.jwt}`

  return url
}

const getFileIconByType = (type = 'file') => {
  switch (type) {
  case 'image':
    return <FileImageOutlined />
  case 'video':
    return <VideoCameraOutlined />
  case 'audio':
    return <AudioOutlined />
  default:
    return <FileOutlined />
  }
}

interface AssetsGridProps {
  assets: EnhancedFsNodeSchema[];
  className?: string;
  selectedItemsIds?: string[];
  selectedBoardsIds?: string[];
  boards?: IFsNodeSchema[];
  onSelect?: (asset: EnhancedFsNodeSchema) => void;
  onClickOutside?: () => void;
  columnWidth?: number;
  columnGap?: number;
  rowHeight?: number;
  rowGap?: number;
  onMenuItemClick?: (actionId: string, selectedBoardsIds: string) => void;
  contextMenuItems?: (asset: IFsNodeSchema) => MenuProps['items'];
  selectedContainerId?: string;
  selectedTargetClassName?: string;
  board?: IFsNodeSchema;
  isItemHoverEnabled?: boolean;
  onContextStateChanged: (isOpened: boolean) => void;
  isOuterContextMenuOpened: boolean;
  onDoubleClick?: (asset: EnhancedFsNodeSchema) => void;
  withoutContextMenu?: boolean;
  withoutOpenPreview?: boolean;
  draggable?: boolean;
  scrollContainerNode?: HTMLDivElement;
}

export const AssetsGrid = ({
  className,
  assets,
  selectedItemsIds = [],
  selectedBoardsIds = [],
  boards = [],
  onSelect,
  onMenuItemClick,
  onDoubleClick,
  contextMenuItems,
  selectedContainerId = 'assets-grid',
  selectedTargetClassName = 'js-asset-card',
  board,
  isItemHoverEnabled = true,
  onContextStateChanged = () => undefined,
  isOuterContextMenuOpened = false,
  withoutContextMenu = false,
  withoutOpenPreview = false,
  draggable = true,
  scrollContainerNode,
}: AssetsGridProps) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const userRole = useAppSelector(getUserRole)
  const draggedItems = useAppSelector(getDraggedItems)
  const listRef = useRef<List>(null)
  const isPublicLink = useAppSelector(getFilesPreviewIsPublicLinkSelector)
  const { isDownloadEnabled, isEditContentEnabled } = useGetEnablePermission()
  const navigate = useNavigate()
  const dragPreviewRef = useRef<HTMLDivElement>(null)

  const [columnCount, setColumnCount] = useState(1)
  const [openCtxDropdownId, setOpenCtxDropdownId] = useState<string | null>(null)
  const [openBtnDropdownId, setOpenBtnDropdownId] = useState<string | null>(null)

  const columnWidth = 362
  const columnGap = 20
  const rowHeight = 224
  const rowGap = 20

  const generateMenuItems = useCallback((asset: EnhancedFsNodeSchema) => {
    if (contextMenuItems) {
      return contextMenuItems(asset)
    }

    return [
      {
        key: BoardAssetsToolbarAction.Open,
        label: 'Open',
        icon: <ArrowsAltOutlined />,
      },
      hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && !isPublicLink && {
        key: BoardAssetsToolbarAction.Share,
        label: 'Share link',
        icon: <LinkOutlined />,
      },
      // {
      //   key: BoardAssetsToolbarAction.MakeFavorite,
      //   label: 'Make favorite',
      //   icon: <StarOutlined />,
      // },
      {
        type: 'divider' as const,
      },
      isDownloadEnabled && {
        key: BoardAssetsToolbarAction.Download,
        label: 'Download',
        icon: <DownloadOutlined />,
      },
      // checkPermission(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER], isEditContentEnabled, isPublicLink) && {
      //   key: BoardAssetsToolbarAction.Copy,
      //   label: 'Copy to board',
      //   icon: <CopyOutlined />,
      // },
      checkPermission(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER], isEditContentEnabled, isPublicLink) && {
        key: BoardAssetsToolbarAction.Move,
        label: 'Move to board',
        icon: <ArrowRightOutlined />,
      },
      checkPermission(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER], isEditContentEnabled, isPublicLink) && {
        key: BoardAssetsToolbarAction.AddToAnotherBoard,
        label: 'Add to board',
        icon: <AppstoreAddOutlined />,
      },
      checkPermission(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER], isEditContentEnabled, isPublicLink) && {
        key: BoardAssetsToolbarAction.Rename,
        label: 'Rename',
        icon: <EditOutlined />,
      },
      hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && FILES_EXTANSIONS_FOR_AI_ASSISTANT_INDEXING.includes(asset.ext) && !isPublicLink && {
        key: BoardAssetsToolbarAction.AllowForAiAssistant,
        label: t('a_common_allowAivard'),
        icon: <DeploymentUnitOutlined />,
        extra: (
          <div onClick={(e) => e.stopPropagation()}>
            <UseAssetForAiAssistantSwitch itemId={asset.id} />
          </div>
        ),
      },
      checkPermission(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER], isEditContentEnabled, isPublicLink) && {
        type: 'divider' as const,
      },
      checkPermission(userRole, [MemberRole.ADMIN, MemberRole.OWNER], isEditContentEnabled, isPublicLink) && {
        key: BoardAssetsToolbarAction.RemoveFromBoard,
        label: 'Remove from board',
        icon: <MinusCircleOutlined />,
      },
      checkPermission(userRole, [MemberRole.ADMIN, MemberRole.OWNER], isEditContentEnabled, isPublicLink) && {
        key: BoardAssetsToolbarAction.Delete,
        label: 'Delete from library',
        icon: <DeleteOutlined />,
        danger: true,
      }
    ]
  }, [contextMenuItems, userRole, isPublicLink, isDownloadEnabled, isEditContentEnabled])

  const hideContextMenus = () => {
    onContextStateChanged(false)
    setOpenCtxDropdownId(null)
    setOpenBtnDropdownId(null)
  }

  useEffect(() => {
    if(selectedItemsIds.length > 0 || isOuterContextMenuOpened) {
      hideContextMenus()
    }
  }, [selectedItemsIds, isOuterContextMenuOpened])

  const onBoardCardClick = (_, item) => {
    hideContextMenus()
    onSelect(item)
  }

  const handleOpenPreview = (asset: EnhancedFsNodeSchema) => {
    dispatch(openFilesPreviewThunk({ items: assets as any, currentItemId: asset.id, totalItemsCount: 1, currentBoardInfo: board, navigate }))
  }

  const handleDoubleClick = (_, asset: EnhancedFsNodeSchema) => {
    hideContextMenus()

    if (onDoubleClick) {
      onDoubleClick(asset)
    }

    if (!withoutOpenPreview) {
      handleOpenPreview(asset)
    }
  }

  const doubleClickCallback = useDoubleClick(onBoardCardClick, handleDoubleClick)

  const handleMenuClick: MenuProps['onClick'] = (data) => {
    hideContextMenus()
    if (data.key === BoardAssetsToolbarAction.Open) {
      const asset = assets.find((asset) => asset.id === data.itemId as string)
      dispatch(assetsActions.unselectAll())
      dispatch(boardsActions.unselectAll())

      if (asset) {
        handleOpenPreview(asset)
      }
    } else {
      onMenuItemClick(data.key, data.itemId as string)
    }
  }

  const getMenuProps = useCallback((asset: IFsNodeSchema) => {
    return {
      items: generateMenuItems(asset),
      onClick: (e) => {
        e.domEvent.stopPropagation()
        handleMenuClick({ ...e, itemId: asset.id })
      },
      className: 'js-asset-card-dropdown'
    }
  }, [generateMenuItems])

  const prepareRows = (assets: EnhancedFsNodeSchema[], columnCount: number) => {
    const rows = []

    for (let i = 0; i < assets.length; i += columnCount) {
      rows.push(assets.slice(i, i + columnCount))
    }

    return rows
  }

  const preventPropagation = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    e.preventDefault()
  }

  const handleDragStart = (e: React.DragEvent, asset: EnhancedFsNodeSchema) => {
    const draggedAssets = selectedItemsIds?.includes(asset.id)
      ? selectedItemsIds.filter(id => assets.find(a => a.id === id))
      : [asset.id]

    const draggedBoards = selectedItemsIds?.includes(asset.id) && selectedBoardsIds.length > 0
      ? selectedBoardsIds.filter(id => boards.find(b => b.id === id))
      : []

    const allDraggedItems = [...draggedAssets, ...draggedBoards]
    dispatch(startDrag(allDraggedItems))

    e.dataTransfer.setData('text/plain', asset.id)
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'mixed',
      id: asset.id,
      assets: draggedAssets,
      boards: draggedBoards,
      name: asset.name
    }))
    e.dataTransfer.effectAllowed = 'move'

    const dragPreviewElement = dragPreviewRef.current?.cloneNode(true) as HTMLElement

    if (dragPreviewElement) {
      const totalCount = draggedBoards.length + draggedAssets.length
      const textElement = dragPreviewElement.querySelector('.js-drag-preview-text') as HTMLElement
      const countElement = dragPreviewElement.querySelector('.js-drag-preview-count') as HTMLElement
      const layoutElement = dragPreviewElement.querySelector('.js-drag-preview-layout') as HTMLElement
      const gridIcon = dragPreviewElement.querySelector('.js-drag-preview-grid-icon') as HTMLElement
      const pictureIcon = dragPreviewElement.querySelector('.js-drag-preview-picture-icon') as HTMLElement

      textElement.textContent = asset.name

      if (totalCount > 1) {
        countElement.textContent = totalCount.toString()
        countElement.style.display = 'flex'
        layoutElement.style.display = 'block'
      } else {
        countElement.style.display = 'none'

        layoutElement.style.display = 'none'
      }

      if (draggedBoards.length > 0) {
        pictureIcon.style.display = 'none'
        gridIcon.style.display = 'block'
      } else {
        pictureIcon.style.display = 'block'
        gridIcon.style.display = 'none'
      }

      dragPreviewElement.style.display = 'block'

      document.body.appendChild(dragPreviewElement)
      e.dataTransfer.setDragImage(dragPreviewElement, 0, 20)

      setTimeout(() => {
        document.body.removeChild(dragPreviewElement)
      }, 0)
    }
  }

  const handleDragEnd = () => {
    dispatch(endDrag())
  }

  const rowRenderer = ({ index, key, style }: { index: number, key: string, style: React.CSSProperties }) => {
    const rows = prepareRows(assets, columnCount)
    const rowItems = rows[index] || []

    return (
      <SRow
        key={key}
        style={style}
      >
        {rowItems.map((asset) => {
          const previewLink = getSmallPreviewLink(asset)
          const isDragging = draggedItems.includes(asset.id)

          return (
            <SAssetCardWrapper
              key={asset.id}
              className={classNames(selectedTargetClassName)}
            >
              <Dropdown
                trigger={['contextMenu']}
                menu={getMenuProps(asset)}
                placement="bottomLeft"
                open={openCtxDropdownId === asset.id}
                onOpenChange={(visible) => {
                  onContextStateChanged(visible)
                  setOpenCtxDropdownId(visible ? asset.id : null)
                  
                  if (visible) {
                    setOpenBtnDropdownId(null)
                  }
                }}
              >
                <SAssetCard
                  id={asset.id}
                  className={classNames(selectedTargetClassName, {
                    selected: selectedItemsIds.includes(asset.id),
                    'hover-enabled': isItemHoverEnabled,
                    'dragging': isDragging
                  })}
                  onClick={doubleClickCallback.bind(null, asset)}
                  draggable={hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && isEditContentEnabled && draggable}
                  onDragStart={(e) => handleDragStart(e, asset)}
                  onDragEnd={handleDragEnd}
                >
                  {!!previewLink && <SAssetCardImage src={previewLink} />}

                  {!previewLink && getFileIconByType(asset.file_info?.type)}

                  {!withoutContextMenu && (
                    <Dropdown
                      trigger={['hover']}
                      menu={getMenuProps(asset)}
                      placement="bottomLeft"
                      open={openBtnDropdownId === asset.id}
                      onOpenChange={(visible) => {
                        onContextStateChanged(visible)
                        setOpenBtnDropdownId(visible ? asset.id : null)
                        setOpenCtxDropdownId(null)
                      }}
                    >
                      <SActionButton
                        onClick={preventPropagation}
                        variant="text"
                        color="default"
                        icon={(
                          <EllipsisOutlined
                            style={{ fontSize: 18 }}
                          />
                        )}
                        style={{ color: 'white', fontSize: 24 }}
                      />
                    </Dropdown>
                  )}

                  <SCardOverlay className={classNames(selectedTargetClassName)}>
                    <SInfoContainer>
                      <SInfoName>
                        {asset.name}
                      </SInfoName>

                      <SInfoMetaData>
                        <SInfoMetaDataItem>
                          {asset?.file_info?.type}
                        </SInfoMetaDataItem>

                      •

                        <SInfoMetaDataItem>
                          {toMb(asset?.file_info?.size)}
                        </SInfoMetaDataItem>

                        <SInfoMetaDataItem>
                          {asset?.file_info?.meta_data?.image && ` • ${asset?.file_info?.meta_data?.image?.width} x ${asset?.file_info?.meta_data?.image?.height}`}
                        </SInfoMetaDataItem>
                      </SInfoMetaData>
                    </SInfoContainer>
                  </SCardOverlay>
                </SAssetCard>
              </Dropdown>
            </SAssetCardWrapper>
          )
        })}
      </SRow>
    )
  }

  return (
    <SAssetsGridContainer
      id={selectedContainerId}
      className={className}
    >
      <DragPreview
        ref={dragPreviewRef}
        hidden
      />

      <WindowScroller scrollElement={scrollContainerNode || document.querySelector('.js-layout-content-area') || window}>
        {({ height, isScrolling, registerChild, onChildScroll, scrollTop }) => (
          <AutoSizer disableHeight>
            {({ width }) => {
              const calculatedColumnCount = Math.max(1, Math.floor((width + 20) / (columnWidth + columnGap)))

              if (calculatedColumnCount !== columnCount) {
                setColumnCount(calculatedColumnCount)
                if (listRef.current) {
                  listRef.current.recomputeRowHeights()
                }
              }

              const rows = prepareRows(assets, columnCount)

              return (
                <div ref={registerChild as any}>
                  <List
                    ref={listRef}
                    autoHeight
                    height={height || window.innerHeight}
                    isScrolling={isScrolling}
                    overscanRowCount={15}
                    rowCount={rows.length}
                    rowHeight={rowHeight + rowGap}
                    rowRenderer={rowRenderer}
                    scrollTop={scrollTop}
                    width={width}
                    onScroll={onChildScroll}
                    style={{ outline: 'none', overflow: 'visible', marginLeft: '-8px' }}
                  />
                </div>
              )
            }}
          </AutoSizer>
        )}
      </WindowScroller>
    </SAssetsGridContainer>
  )
}

const SAssetsGridContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: visible;
  position: relative;
`



const SRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  padding-left: 8px;
`

const SAssetCardWrapper = styled.div`
  width: 362px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const SCardOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%);
  border-radius: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;
  user-select: none;
`

const SActionButton = styled(Button)`
  && {
    width: 24px;
    height: 24px;
    display: none;
    position: absolute;
    top: 14px;
    right: 16px;
    z-index: 1;
    background-color: var(--color-gray-100) !important;
    color: black !important;
    

    ul {
      min-width: 186px;
    }

    button {
      width: 24px;
      height: 24px;
    }
    
    &[ant-click-animating-without-extra-node]:after {
      animation: none !important;
    }
  }
`

const SAssetCard = styled.div`
  width: 100%;
  height: 224px;
  background-color: rgba(245, 245, 245, 1);
  border-radius: 8px;
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
  
  &.hover-enabled:hover {
    ${SCardOverlay} {
      opacity: 1;
      background: linear-gradient(0deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.80) 100%);
    }

    ${SActionButton} {
      display: flex;
    }
  }

  &.selected {
    outline-offset: 2px;
    outline: var(--ant-color-primary-text-active) solid 2px;
    background-color: var(--ant-color-primary-bg-hover);

    ${SActionButton} {
      display: flex;
    }
  }

  &.dragging {
    opacity: 0.24;

    ${SActionButton} {
      display: none;
    }
  }

  &[draggable="true"]:active {
    cursor: grabbing;
  }
`

const SAssetCardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
`

const SInfoContainer = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
`

const SInfoName = styled.p`
  font-size: 16px;
  font-weight: 500;
  color: rgba(255, 255, 255, 1);
`

const SInfoMetaData = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  color: rgba(255, 255, 255, 1);
`

const SInfoMetaDataItem = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 1);
  line-height: 1;
`
